import { useState, type ReactElement } from 'react';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { newIntent, useApiAction, useApiList, useApiResource } from '../../lib/api/queries';
import { useSession } from '../../lib/auth/session';
import type { AttributionSummary, ConsentState, CustomerBlock } from '../../lib/api/types';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select } from '../../ui/primitives';
import { EmptyState, ErrorState, LoadingState, StateIcon, SuccessNote } from '../../ui/states';

/** `CUS-04` — per-expo status and enrolment. */
export function CustomerEnrolments(): ReactElement {
  const { t } = useI18n();
  const [expoId, setExpoId] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [linkSlug, setLinkSlug] = useState('');
  const enroll = useApiAction<{ enrollment_id?: string; status?: string }>([]);

  return (
    <>
      <PageTitle title={t('cus.enrollments.title')}>{t('portal.customer.desc')}</PageTitle>
      <Card title={t('cus.enroll')}>
        <Field label="expo_id" name="enroll-expo" value={expoId} onChange={setExpoId} ltr required />
        <Field
          label="invitation_code"
          name="enroll-invitation"
          value={invitationCode}
          onChange={setInvitationCode}
          ltr
        />
        <Field label="public_link_slug" name="enroll-link" value={linkSlug} onChange={setLinkSlug} ltr />
        {enroll.error ? <ErrorState error={enroll.error} /> : null}
        {enroll.data?.enrollment_id ? (
          <SuccessNote>
            <Ltr>enrollment_id={enroll.data.enrollment_id}</Ltr>
          </SuccessNote>
        ) : null}
        <button
          className="btn"
          type="button"
          disabled={enroll.isPending || expoId === ''}
          onClick={() =>
            enroll.mutate(newIntent({
              path: `/v1/expos/${expoId}/enrollments`,
              body: {
                invitation_code: invitationCode,
                public_link_slug: linkSlug,
                visitor_id: '',
              },
            }))
          }
        >
          {t('cus.enroll')}
        </button>
      </Card>
    </>
  );
}

const CONSENT_KINDS = ['terms', 'privacy', 'marketing', 'analytics', 'recording'];

/** `CUS-12` — the privacy centre. */
export function CustomerConsents(): ReactElement {
  const { t, formatDateTime } = useI18n();
  const path = '/v1/me/consents';
  const consents = useApiResource<{ items?: ConsentState[]; version?: number }>(path);
  const save = useApiAction([path]);
  const [kind, setKind] = useState(CONSENT_KINDS[0] ?? 'terms');
  const [decision, setDecision] = useState('granted');
  const [textVersion, setTextVersion] = useState('v1');

  const items = consents.data?.data.items ?? [];

  return (
    <>
      <PageTitle title={t('cus.consents.title')}>{t('cus.consents.desc')}</PageTitle>
      <Card title={t('cus.consents.title')}>
        {consents.isLoading ? <LoadingState /> : null}
        {consents.error ? <ErrorState error={consents.error} onRetry={() => void consents.refetch()} /> : null}
        {consents.data ? (
          <DataTable<ConsentState>
            caption={t('cus.consents.title')}
            rows={items}
            rowKey={(c) => c.kind}
            empty={<EmptyState>{t('cus.consents.desc')}</EmptyState>}
            columns={[
              { key: 'kind', header: t('table.kind'), cell: (c) => <Ltr>{c.kind}</Ltr> },
              {
                key: 'decision',
                header: 'decision',
                cell: (c) => <Badge tone={c.decision === 'granted' ? 'ok' : 'neutral'}>{c.decision}</Badge>,
              },
              { key: 'text', header: 'text_version', cell: (c) => <Ltr>{c.text_version}</Ltr> },
              { key: 'surface', header: 'captured_surface', cell: (c) => <Ltr>{c.captured_surface}</Ltr> },
              { key: 'at', header: t('table.created'), cell: (c) => formatDateTime(c.occurred_at) },
            ]}
          />
        ) : null}

        <Select
          label="kind"
          name="consent-kind"
          value={kind}
          options={CONSENT_KINDS.map((k) => ({ value: k, label: k }))}
          onChange={setKind}
        />
        <Select
          label="decision"
          name="consent-decision"
          value={decision}
          options={['granted', 'withdrawn'].map((d) => ({ value: d, label: d }))}
          onChange={setDecision}
        />
        <Field label="text_version" name="consent-text" value={textVersion} onChange={setTextVersion} ltr />
        {save.error ? <ErrorState error={save.error} /> : null}
        <button
          className="btn"
          type="button"
          disabled={save.isPending}
          onClick={() =>
            save.mutate(newIntent({
              path,
              method: 'PUT',
              body: { consents: [{ kind, decision, text_version: textVersion }] },
            }))
          }
        >
          {t('action.save')}
        </button>
      </Card>
    </>
  );
}

/** `CUS-16` — who is serving me, and `CUS-13` sessions. */
export function CustomerAttribution(): ReactElement {
  const { t } = useI18n();
  const summary = useApiList<AttributionSummary>('/v1/me/attribution-summary');
  const blocks = useApiList<CustomerBlock>('/v1/me/blocks');
  const session = useSession();
  const revoke = useApiAction<{ revoked?: number }>([]);

  const rows = summary.data?.items ?? [];

  return (
    <>
      <PageTitle title={t('cus.attribution.title')}>{t('cus.attribution.desc')}</PageTitle>
      <Card title={t('cus.attribution.title')}>
        {summary.isLoading ? <LoadingState /> : null}
        {summary.error ? <ErrorState error={summary.error} onRetry={() => void summary.refetch()} /> : null}
        {summary.data ? (
          <DataTable<AttributionSummary>
            caption={t('cus.attribution.title')}
            rows={rows}
            rowKey={(s) => s.expo_id}
            // Never a blank: a customer with no owner is served directly by the floor team.
            empty={<EmptyState title={t('cus.attribution.none')}>{t('cus.attribution.desc')}</EmptyState>}
            columns={[
              { key: 'expo', header: 'expo_id', cell: (s) => <Ltr>{s.expo_id.slice(0, 8)}</Ltr> },
              {
                key: 'brokerage',
                header: t('table.name'),
                cell: (s) => s.brokerage_display_name ?? t('cus.attribution.none'),
              },
              { key: 'broker', header: 'broker', cell: (s) => s.broker_first_name ?? t('table.none') },
              { key: 'source', header: 'source_type', cell: (s) => <Ltr>{s.source_type}</Ltr> },
            ]}
          />
        ) : null}
      </Card>

      <Card title={t('cus.blocks.title')}>
        {blocks.error ? <ErrorState error={blocks.error} /> : null}
        {blocks.data ? (
          <DataTable<CustomerBlock>
            caption={t('cus.blocks.title')}
            rows={blocks.data.items ?? []}
            rowKey={(b) => b.id}
            empty={<EmptyState />}
            columns={[
              { key: 'user', header: 'blocked_user_id', cell: (b) => <Ltr>{b.blocked_user_id.slice(0, 8)}</Ltr> },
              { key: 'reason', header: 'reason_code', cell: (b) => b.reason_code ?? t('table.none') },
            ]}
          />
        ) : null}
      </Card>

      <Card title={t('cus.sessions.title')} description={t('cus.sessions.desc')}>
        {/* Revoking the current session signs the user out — stated FIRST. */}
        <div className="state state--degraded">
          <p className="state__title">
            <StateIcon kind="warning" />
            {t('cus.sessions.desc')}
          </p>
        </div>
        {revoke.error ? <ErrorState error={revoke.error} /> : null}
        {revoke.data ? (
          <SuccessNote>
            <span className="numeral">{revoke.data.revoked ?? 0}</span>
          </SuccessNote>
        ) : null}
        <button
          className="btn btn--destructive"
          type="button"
          disabled={revoke.isPending}
          onClick={() => revoke.mutate(newIntent({ path: '/v1/auth/sessions:revoke', body: { all_others: true } }))}
        >
          {t('cus.sessions.revokeOthers')}
        </button>
        <button className="btn btn--secondary" type="button" onClick={() => void session.signOut()}>
          {t('nav.signOut')}
        </button>
      </Card>
    </>
  );
}

/**
 * `CUS-06`/`CUS-15` — the shortlist.
 *
 * There is no favourites or shortlist route on this branch: `GET /v1/me/favorites` and
 * `GET /v1/me/shortlist` land with P6/P9. The screen therefore exists with its real structure and
 * shows the server's own answer for the route it would call — no placeholder rows, no sample data.
 */
export function CustomerShortlist(): ReactElement {
  const { t } = useI18n();
  const favourites = useApiList<{ id: string }>('/v1/me/favorites');

  return (
    <>
      <PageTitle title={t('cus.shortlist.title')}>{t('portal.customer.desc')}</PageTitle>
      <Card title={t('cus.shortlist.title')}>
        {favourites.isLoading ? <LoadingState /> : null}
        {favourites.error ? <ErrorState error={favourites.error} awaitingPackage="P6/P9" /> : null}
        {favourites.data ? (
          <DataTable<{ id: string }>
            caption={t('cus.shortlist.title')}
            rows={favourites.data.items ?? []}
            rowKey={(f) => f.id}
            empty={<EmptyState />}
            columns={[{ key: 'id', header: t('table.identifier'), cell: (f) => <Ltr>{f.id}</Ltr> }]}
          />
        ) : null}
      </Card>
    </>
  );
}
